/**
 * QueueGeneratedImages - Draggable generated images from a queue item
 *
 * Grid with thumbnails that can be dragged to channel slots.
 * Must be rendered INSIDE a DndContext to enable drag & drop.
 */

import { useTranslation } from 'react-i18next'
import { ImageIcon, GripVertical } from 'lucide-react'
import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import type { QueueImage } from '@/lib/api/image-queue'

interface QueueGeneratedImagesProps {
  images: QueueImage[]
}

export function QueueGeneratedImages({ images }: QueueGeneratedImagesProps) {
  const { t } = useTranslation()
  const draggableImages = images.filter((img) => img.asset_id)

  return (
    <div className="border border-neutral-200 dark:border-neutral-700 rounded-xl overflow-hidden">
      <div className="px-4 py-2.5 bg-primary-50 dark:bg-primary-900/20 border-b border-neutral-200 dark:border-neutral-700">
        <h3 className="text-sm font-semibold text-primary-700 dark:text-primary-300">
          {t('readiness.generatedImages')} ({images.length})
        </h3>
        <p className="text-xs text-primary-600/70 dark:text-primary-400/70">
          {t('common.dragDropOrClick')}
        </p>
      </div>
      <div className="p-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
        {images.map((image) => (
          draggableImages.includes(image) ? (
            <DraggableQueueImage key={image.id} image={image} />
          ) : (
            <StaticQueueImage key={image.id} image={image} />
          )
        ))}
      </div>
    </div>
  )
}

function DraggableQueueImage({ image }: { image: QueueImage }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `queue-${image.asset_id}`,
    data: {
      asset: {
        asset_id: image.asset_id,
        link_id: `queue-${image.asset_id}`,
        channel_id: null,
        channel_name: 'Queue',
        channel_type: null,
        slot: 0,
        sort_number: 0,
        alt_text: null,
        s3_key_original: image.s3_key || null,
        s3_url: image.output_url,
        width: image.width || 0,
        height: image.height || 0,
        mime_type: null,
        content_hash: null,
      },
      channelKey: 'library',
      channelId: null,
      fromLibrary: true,
    },
  })

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className="group relative aspect-square rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden bg-neutral-50 dark:bg-neutral-800 cursor-grab active:cursor-grabbing"
    >
      <img
        src={image.output_url}
        alt="Generiertes Bild"
        className="w-full h-full object-cover pointer-events-none"
        loading="lazy"
      />
      <div className="absolute top-1 left-1 p-0.5 bg-black/40 rounded opacity-0 group-hover:opacity-100 transition-opacity">
        <GripVertical className="h-3 w-3 text-white" />
      </div>
    </div>
  )
}

function StaticQueueImage({ image }: { image: QueueImage }) {
  const { t } = useTranslation()
  return (
    <div className="group relative aspect-square rounded-lg border border-dashed border-neutral-300 dark:border-neutral-600 overflow-hidden bg-neutral-50 dark:bg-neutral-800 opacity-50" title={t('readiness.noAssetLinked')}>
      {image.output_url ? (
        <img src={image.output_url} alt={t('readiness.generatedImage')} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <ImageIcon className="h-8 w-8 text-neutral-300 dark:text-neutral-600" />
        </div>
      )}
    </div>
  )
}
